import { Component, ElementRef, EventEmitter, Input, OnInit, ViewChild } from '@angular/core';
import { CognitoUser, FnolRequest, FnolRequestStatus, GeneralConfirmationModalResult, TasksLabels } from "../../../models";
import { CognitoUsersService } from "../../../services/cognito-users.service";
import { LoadingOverlayRef, LoadingService } from "../../../services/loading.service";
import { ApiHttpService } from "../../../services/api-http.service";
import { debounceTime } from "rxjs";
import { MatDialog } from "@angular/material/dialog";
import { TaskListModalComponent } from "../../task/task-list-modal/task-list-modal.component";
import { MatSnackBar } from "@angular/material/snack-bar";
import { CustomSnackbarErrorComponent } from "../../custom-snackbar-error/custom-snackbar-error.component";
import { TranslateService } from "@ngx-translate/core";
import { getRandomString, getStatusCategory, openOrDownload, statusString } from 'src/app/helpers/utility';
import { HttpClient } from '@angular/common/http';
import { GeneralConfirmationModalComponent } from '../../general-confirmation-modal/general-confirmation-modal.component';


@Component({
  selector: 'CPF-fnol-request-side-bar',
  templateUrl: './fnol-request-side-bar.component.html',
  styleUrls: ['./fnol-request-side-bar.component.scss']
})
export class FnolRequestSideBarComponent implements OnInit {
  @Input()
  fnolRequest: FnolRequest | null = null;
  public usersList: CognitoUser[] = [];
  private dropdownValueChange: EventEmitter<string> = new EventEmitter();
  public tasksLabels = TasksLabels;
  public disableMarkMultipleButton = false;

  @ViewChild('fileInput') fileInput: ElementRef<HTMLInputElement> | undefined;


  constructor(private cognitoUsersService: CognitoUsersService,
              private apiHttpService: ApiHttpService,
              private loadingService: LoadingService,
              private dialog: MatDialog,
              private snackBar: MatSnackBar,
              private translateService: TranslateService,
              private http: HttpClient) {
  }

  async ngOnInit() {
    this.usersList = await this.cognitoUsersService.getCognitoUsers();

    this.dropdownValueChange.pipe(debounceTime(1000)).subscribe(() => {
      this.save();
    });
  }

  dropdownValueChanged() {
    this.dropdownValueChange.emit();
  }

  isUnclosedTasks() {
    if (!this.fnolRequest || !this.fnolRequest.tasks) {
      return false;
    }

    return this.fnolRequest.tasks.some(t => !t.closingDate);
  }

  isLabelChecked(label: TasksLabels) {
    if (!this.fnolRequest || !this.fnolRequest.labels) {
      return false;
    }

    return this.fnolRequest.labels.includes(label);
  }

  checkLabel(label: TasksLabels) {
    if (!this.fnolRequest) {
      return;
    }

    if (!this.fnolRequest.labels) {
      this.fnolRequest.labels = [];
    }

    if (this.fnolRequest.labels.includes(label)) {
      this.fnolRequest.labels = this.fnolRequest.labels.filter(l => l !== label);
    } else {
      this.fnolRequest.labels.push(label);
    }

    this.dropdownValueChanged();
  }

  async save() {
    if (!this.fnolRequest) {
      return;
    }

    const loadingRef: LoadingOverlayRef = this.loadingService.open();
    try {
      const res = await this.apiHttpService.updateFnolRequest(this.fnolRequest);
    } catch (e) {
      console.error(`Error while saving the FNOL request: ${e}`);
    } finally {
      loadingRef.close();
    }
  }

  openTasksList() {
    if (!this.fnolRequest) {
      return;
    }

    if (!this.fnolRequest.tasks) {
      this.fnolRequest.tasks = [];
    }

    this.fnolRequest!.tasks = this.fnolRequest!.tasks.map(t => ({
      ...t,
      includeInComment: t.includeInComment ?? false
    }));
    const dialogRef = this.dialog.open(TaskListModalComponent, {
      data: this.fnolRequest,
      maxWidth: '1000px',
      disableClose: true
    });
    dialogRef.afterClosed().subscribe(() => {
      this.save();
    });
  }

  completeFNOLRequestWithoutProcessing() {
    if (this.fnolRequest) {
      this.fnolRequest.processingStatus = FnolRequestStatus.NO_PROCESSING;
      this.save();
    }
  }

  reopenFNOLRequest() {
    if (this.fnolRequest) {
      this.fnolRequest.processingStatus = FnolRequestStatus.INVOICE_PARSED_CONFIRMED;
      this.save();
    }
  }

  async duplicate() {
    if (!this.fnolRequest) {
      return;
    }

    try {
      const res = await this.apiHttpService.duplicateFnolRequest(this.fnolRequest.id);
      if (this.fnolRequest.clonedRequestIds) {
        this.fnolRequest.clonedRequestIds.push(res.id);
      } else {
        this.fnolRequest.clonedRequestIds = [res.id];
      }
    } catch (e: any) {
      this.snackBar.openFromComponent(CustomSnackbarErrorComponent, {
        data: {
          error: e.message
        }
      });
    }
  }

  linkToFnolRequestById(parentRequestId: string) {
    return `/platform/fnol-request/${parentRequestId}`;
  }

  async markRequestsAsMultipleClaims() {
    if (!this.fnolRequest) {
      return;
    }
    try {
      this.disableMarkMultipleButton = true;
      const updatedRequests: FnolRequest[] = await this.apiHttpService.markMultipleClaims(this.fnolRequest!.fnolEmail);
      let updatedLables = updatedRequests.find(request => request.id === this.fnolRequest?.id)?.labels;
      if (updatedLables) {
        this.fnolRequest.labels = updatedLables;
      }
    } finally {
      this.disableMarkMultipleButton = false;
    }
  }

  getStatusString(status: FnolRequestStatus): string {
    return statusString(status);
  }

  getStatusCategory(status: FnolRequestStatus): string {
    return getStatusCategory(status);
  }

  // Function to handle the file input change event
  async onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      console.log('received file for upload')
      const file = input.files[0];
      // Perform additional actions with the selected file
      console.log('Selected file:', file);
      if (this.fnolRequest?.id && file.name) {
        let uploadName = getRandomString(4) + '_' + file.name;
        try {
          const presignedUrl = (await this.apiHttpService.getFileUploadUrl(this.fnolRequest.id, uploadName)).Location;
          // Upload the file to S3
          console.log('received presigend URL', presignedUrl)
          const response = await this.http.put(presignedUrl, file, {
            headers: {
              'Content-Type': file.type
            }
          }).toPromise();
          console.log('repsonse', response)
          if (!this.fnolRequest.additionalDocuments) {
            this.fnolRequest.additionalDocuments = [];
          }
          this.fnolRequest.additionalDocuments.push({ s3Id: uploadName })
          await this.apiHttpService.updateFnolRequest(this.fnolRequest)

        } catch (e) {
          console.error(e);
        }
      }

    }
  }

  uploadAdditionalDocument() {
    this.fileInput?.nativeElement.click();
  }

  async openOrDownloadAdditionalDocument(s3Id: string) {
    if (this.fnolRequest?.id) {
        openOrDownload(this.fnolRequest.id, s3Id, this.apiHttpService);
    }
  }

  deleteAdditionalDocument(s3Id: string) {
    if (this.fnolRequest?.id) {
      this.fnolRequest.additionalDocuments = this.fnolRequest.additionalDocuments?.filter(d => d.s3Id !== s3Id);
      this.save();
    }
  }

  deleteAdditionalDocumentConfirmation(s3Id: string) {
    const dialogRef = this.dialog.open(GeneralConfirmationModalComponent, {
      autoFocus: false,
      restoreFocus: false,
      data: {
        titleKey: 'general-confirmation-modal.modals.delete-additional-document-confirmation.title',
        messageKey: 'general-confirmation-modal.modals.delete-additional-document-confirmation.message',
        confirmButton: GeneralConfirmationModalResult.Confirm,
        cancelButton: GeneralConfirmationModalResult.Cancel,
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === GeneralConfirmationModalResult.Confirm) {
        this.deleteAdditionalDocument(s3Id);
      }
    });
  }
}
